import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import {
  theme,
  useResponsive,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { Loyalty, LOYALTY_PALETTES } from '@order/features/loyalty';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useLoyaltyPremiumStatus } from '../../hooks';
import { usePostPurchaseContent } from './hooks';
import { usePostPurchaseModalState } from './usePostPurchaseModalState';

export const LoyaltyPostPurchaseModal = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { minWidth, match } = useResponsive();
  const { track } = useTelemetry();

  const { isPremium } = useLoyaltyPremiumStatus();

  const [content] = usePostPurchaseContent();
  const { isVisible, orderId, hideModal } = usePostPurchaseModalState();

  const navigateToOrderStatus = useCallback(() => {
    if (!orderId) return;

    // NOTE: We use a short delay on iOS because of a known issue during
    //       interactions between two modals.
    setTimeout(
      () => {
        if (isExpoRouterEnabled) {
          router?.navigate({
            pathname: '/order-status/[orderId]',
            params: { orderId },
          });
          return;
        }

        navigation?.navigate('Modal', {
          screen: 'OrderStatus',
          params: { orderId },
        });
      },
      Platform.select({ ios: 750, default: 0 }),
    );
  }, [isExpoRouterEnabled, navigation, orderId, router]);

  const onPressCta = useCallback(() => {
    if (!orderId) return;

    track('points.confirmation.orderstatus.cta_tapped');

    hideModal();
    navigateToOrderStatus();
  }, [orderId, track, hideModal, navigateToOrderStatus]);

  const onPressTextLink = useCallback(() => {
    track('points.confirmation.sgrewards.cta_tapped');
    hideModal();

    if (isExpoRouterEnabled) {
      router?.navigate('/loyalty');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'LoyaltyTab',
      params: { screen: 'LoyaltyHome' },
    });
  }, [hideModal, isExpoRouterEnabled, navigation, router, track]);

  const onCloseButtonPress = useCallback(() => {
    track('points.confirmation.dismiss.cta_tapped');
    hideModal();
  }, [hideModal, track]);

  return (
    <Loyalty.PostPurchase.Container
      visible={isVisible}
      isPremium={isPremium}
      onRequestClose={onCloseButtonPress}
      onCloseButtonPress={onCloseButtonPress}
    >
      <Loyalty.PostPurchase.Hero
        image={content.image}
        style={match([styles.imageXS, styles.imageDefault])}
      />

      <Loyalty.PostPurchase.Body
        style={[
          match([styles.bodyXS, styles.bodySM]),
          isPremium && minWidth.isSM ? styles.premiumBorder : undefined,
        ]}
      >
        <Loyalty.PostPurchase.Title
          style={[
            isPremium ? LOYALTY_PALETTES.premiumFgColor : {},
            match([styles.titleXS, styles.titleSM]),
          ]}
        >
          {content.title}
        </Loyalty.PostPurchase.Title>

        <Loyalty.PostPurchase.Subtitle>
          {content.description}
        </Loyalty.PostPurchase.Subtitle>

        <Loyalty.PostPurchase.CTA
          palette={isPremium ? 'premium' : 'sweetCorn'}
          onPress={onPressCta}
        />

        <Loyalty.PostPurchase.TextLink onPress={onPressTextLink}>
          <FormattedMessage {...messages.link} />
        </Loyalty.PostPurchase.TextLink>
      </Loyalty.PostPurchase.Body>
    </Loyalty.PostPurchase.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bodyXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  bodySM: {
    flex: 1,
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
  },
  imageXS: {
    width: '100%',
    height: 350,
  },
  imageDefault: {
    width: 228,
  },
  titleXS: {
    marginTop: theme.spacing['10'],
  },
  titleSM: {
    marginTop: theme.spacing['20'],
  },
  premiumBorder: {
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.colors.CREAM,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  link: {
    defaultMessage: 'Go to SG Rewards',
    description: 'Loyalty Post Purchase | Modal | SgRewards Text Link',
  },
});
